import { FC, Fragment } from 'react';
import cn from 'classnames';

import { DataButton, DataLink } from 'components/Shared/SharedComponents';
import styles from 'components/Footer/Copyright/Copyright.module.scss';
import classes from 'styles/_commonClasses.module.scss';
import {
  FooterCopyrightInterface,
  CopyrightSlug as Slug,
  CopyrightLinksInterface,
  CopyrightLinkInterface,
} from 'components/Footer/Copyright/declarations';
import { capitalizeFirstLetter } from 'lib/text.service';
import { FooterType } from 'constants/enums';
import { ONE_DAY_IN_MS } from 'constants/constants';

/* Convert ISO date string to milliseconds, return 0 if missing */
const toMs = (iso?: string | null) => (iso ? new Date(iso).getTime() : 0);

/* Nested component */
const CopyrightLink: FC<CopyrightLinkInterface> = ({ link, policyUpdateDate }) => {
  if (link.slug === Slug.PERSONAL_INFO) {
    const openInNewWindow = () => {
      const width = 730;
      const height = 610;
      const left = ((window.innerWidth - width) / 2) + window.screenX;
      const top = ((window.innerHeight - height) / 2) + window.screenY;
      window.open(link.src || '/',
        'chromeWindow',
        `width=${width
        },height=${height
        },top=${top
        },left=${left}`);
    };

    return (
      <DataButton
        link={link}
        onClick={openInNewWindow}
        className={cn(styles.contentLinksButton, classes.textBold)}
      >
        {link.title}
      </DataButton>
    );
  }

  const isShouldOpenInNewTab = link.src?.includes('#') && (link.src as string).startsWith('/');
  const target = isShouldOpenInNewTab ? '_blank' : undefined;

  const now = Date.now();
  const updateWindow = ONE_DAY_IN_MS * (policyUpdateDate?.timeToKeepUpdatedLabel || 7);
  const isPrivacyPolicyUpdated = link.slug === Slug.PRIVACY_POLICY
  && (toMs(policyUpdateDate?.privacyPolicyLastUpdate) + updateWindow) >= now;

  const isTermsOfUseUpdated = link.slug === Slug.TERMS_OF_USE
  && (toMs(policyUpdateDate?.termsOfUseLastUpdate) + updateWindow) >= now;

  const title = (isPrivacyPolicyUpdated || isTermsOfUseUpdated)
    ? `${link.title} - Updated`
    : link.title;

  return (
    <DataLink
      href={link.src!}
      link={link}
      className={cn(styles.contentLinksLink, classes.textBold)}
      target={target}
    >
      {title?.trim()}
    </DataLink>
  );
};

/* Nested component */
const CopyrightLinks: FC<CopyrightLinksInterface> = ({ copyrightLinks, policyUpdateDate }) => {
  const length = copyrightLinks?.length;

  if (length > 0) {
    return (
      <>
        {copyrightLinks.map((link, i) => (
          <Fragment key={link.entryName}>
            <CopyrightLink link={link} policyUpdateDate={policyUpdateDate} />
            {i + 1 !== length && <span className={styles.contentLinksDivider} />}
          </Fragment>
        ))}
      </>
    );
  }

  return null;
};

/* Main component */
const Copyright: FC<FooterCopyrightInterface> = ({
  copyrightDescription,
  copyrightLinks,
  footerType,
  isMobileView,
  desktopSiteRequest,
  mobileSiteRequest,
  isDesktopRequested,
  requestAnotherSiteView,
  policyUpdateDate,
}) => {
  const switchViewButtonText = (isDesktopRequested)
    ? mobileSiteRequest
    : desktopSiteRequest;
  const isVisibleSwitchViewButton = process.env.NEXT_PUBLIC_KEEP_REQUEST_DESKTOP === 'true' && (isMobileView !== isDesktopRequested)
    && (footerType !== FooterType.LIGHT);
  const copyrightLinksComponent = (
    <div className={styles.contentLinks}>
      <CopyrightLinks
        policyUpdateDate={policyUpdateDate}
        copyrightLinks={copyrightLinks}
      />
    </div>
  );
  const copyrightDescriptionComponent = (
    <span className={styles.contentDescription}>
      {copyrightDescription}
    </span>
  );

  return (
    <div className={cn(styles.copyright, styles[`copyright${capitalizeFirstLetter(footerType)}`])}>
      {isVisibleSwitchViewButton && (
        <button
          type="button"
          className={styles.button}
          onClick={requestAnotherSiteView}
          onKeyPress={requestAnotherSiteView}
        >
          {switchViewButtonText}
        </button>
      )}
      <div className={styles.content}>
        {
          isMobileView
            ? (
              <>
                {copyrightLinksComponent}
                {copyrightDescriptionComponent}
              </>
            )
            : (
              <>
                {copyrightDescriptionComponent}
                {copyrightLinksComponent}
              </>
            )
        }
      </div>
    </div>
  );
};

export default Copyright;
